import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { MainLayout } from '@/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Activity, Zap, Monitor, RotateCcw } from 'lucide-react';
import * as THREE from 'three';

// 旋转的测试立方体
const RotatingCube = () => {
  const meshRef = useRef<THREE.Mesh>(null);
  
  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta;
      meshRef.current.rotation.y += delta * 0.5;
    }
  });

  return (
    <mesh ref={meshRef} position={[0, 0, 0]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="hotpink" />
    </mesh>
  );
};

// 系统信息显示
const SystemInfo = () => {
  const [webglInfo, setWebglInfo] = useState<{
    webgl: boolean;
    webgl2: boolean;
    renderer?: string;
    vendor?: string;
    version?: string;
  }>({
    webgl: false,
    webgl2: false
  });

  useEffect(() => {
    const checkWebGL = () => {
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        const gl2 = canvas.getContext('webgl2');
        
        let info = {
          webgl: !!gl,
          webgl2: !!gl2
        };

        if (gl) {
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          if (debugInfo) {
            info = {
              ...info,
              renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
              vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
            };
          }
          info.version = gl.getParameter(gl.VERSION);
        }

        setWebglInfo(info);
      } catch (error) {
        console.error('WebGL检测失败:', error);
        setWebglInfo({ webgl: false, webgl2: false });
      }
    };

    checkWebGL();
  }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2 flex items-center">
            <Monitor className="h-4 w-4 mr-2" />
            浏览器兼容性
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>WebGL:</span>
              <Badge variant={webglInfo.webgl ? "default" : "destructive"}>
                {webglInfo.webgl ? '✅ 支持' : '❌ 不支持'}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span>WebGL2:</span>
              <Badge variant={webglInfo.webgl2 ? "default" : "destructive"}>
                {webglInfo.webgl2 ? '✅ 支持' : '❌ 不支持'}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span>设备像素比:</span>
              <span>{window.devicePixelRatio || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2 flex items-center">
            <Zap className="h-4 w-4 mr-2" />
            系统状态
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>用户代理:</span>
              <span className="text-xs truncate">{navigator.userAgent.substring(0, 30)}...</span>
            </div>
            <div className="flex justify-between">
              <span>当前时间:</span>
              <span>{new Date().toLocaleTimeString()}</span>
            </div>
            <div className="flex justify-between">
              <span>页面加载:</span>
              <Badge variant="default">✅ 完成</Badge>
            </div>
          </div>
        </div>
      </div>

      {webglInfo.renderer && (
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">GPU信息</h4>
          <div className="space-y-1 text-sm">
            <div><strong>渲染器:</strong> {webglInfo.renderer}</div>
            <div><strong>厂商:</strong> {webglInfo.vendor}</div>
            <div><strong>版本:</strong> {webglInfo.version}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const ThreeTest = () => {
  const [isRendering, setIsRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [sceneReady, setSceneReady] = useState(false);

  const handleSceneCreated = () => {
    console.log('Three.js场景创建成功');
    setSceneReady(true);
    setIsRendering(true);
  };

  const handleError = (error: any) => {
    console.error('Three.js渲染错误:', error);
    setRenderError(error.message || '未知渲染错误');
    setIsRendering(false);
  };

  const resetScene = () => {
    setRenderError(null);
    setIsRendering(false);
    setSceneReady(false);
    
    // 强制重新渲染组件
    setTimeout(() => {
      setIsRendering(true);
    }, 100);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* 页面标题 */}
        <div className="tech-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Activity className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">Three.js 基础渲染测试</h1>
                <p className="text-muted-foreground">
                  验证WebGL和Three.js基础功能是否正常工作
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge variant={sceneReady ? "default" : "destructive"}>
                {sceneReady ? '✅ 场景就绪' : '⏳ 初始化中'}
              </Badge>
              
              <Button
                variant="outline" 
                size="sm"
                onClick={resetScene}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                重置场景
              </Button>
            </div>
          </div>
        </div>

        {/* 错误提示 */}
        {renderError && (
          <div className="tech-card p-4 border-red-500/50 bg-red-500/5">
            <h3 className="text-lg font-semibold text-red-400 mb-2">渲染错误</h3>
            <p className="text-red-300">{renderError}</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3"
              onClick={resetScene}
            >
              重试
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 3D渲染区域 */}
          <Card className="h-[500px]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                基础3D渲染测试
                <Badge variant={isRendering ? "default" : "destructive"}>
                  {isRendering ? '🎬 渲染中' : '⏸️ 停止'}
                </Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                一个简单的旋转立方体，用于测试Three.js基础功能
              </p>
            </CardHeader>
            <CardContent className="h-[400px] p-0">
              {!renderError && (
                <Canvas 
                  camera={{ position: [3, 3, 3], fov: 50 }}
                  onCreated={handleSceneCreated}
                  onError={handleError}
                  style={{ background: 'linear-gradient(135deg, #1e1e2e 0%, #2d2d3a 100%)' }}
                >
                  {/* 基础光照 */}
                  <ambientLight intensity={0.5} />
                  <directionalLight position={[5, 5, 5]} intensity={0.8} />
                  
                  {/* 测试立方体 */}
                  <RotatingCube />
                  
                  {/* 参考网格 */}
                  <gridHelper args={[10, 10]} />
                  
                  {/* 相机控制 */}
                  <OrbitControls 
                    enablePan={true}
                    enableZoom={true}
                    enableRotate={true}
                  />
                </Canvas>
              )}
              
              {renderError && (
                <div className="flex items-center justify-center h-full bg-muted">
                  <div className="text-center">
                    <div className="text-4xl mb-4">❌</div>
                    <p className="text-muted-foreground">3D渲染失败</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 系统信息 */}
          <Card className="h-[500px]">
            <CardHeader>
              <CardTitle>系统兼容性检查</CardTitle>
              <p className="text-sm text-muted-foreground">
                检查浏览器对WebGL和Three.js的支持情况
              </p>
            </CardHeader>
            <CardContent className="h-[400px] overflow-y-auto">
              <SystemInfo />
            </CardContent>
          </Card>
        </div>

        {/* 诊断信息 */}
        <Card>
          <CardHeader>
            <CardTitle>诊断步骤</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-2">
                <Badge variant={typeof window !== 'undefined' ? "default" : "destructive"}>1</Badge>
                <span>检查浏览器环境: {typeof window !== 'undefined' ? '✅ 正常' : '❌ 异常'}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Badge variant={sceneReady ? "default" : "destructive"}>2</Badge>
                <span>Three.js场景初始化: {sceneReady ? '✅ 成功' : '⏳ 进行中'}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Badge variant={isRendering ? "default" : "destructive"}>3</Badge>
                <span>实时渲染: {isRendering ? '✅ 正常' : '❌ 停止'}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Badge variant={!renderError ? "default" : "destructive"}>4</Badge>
                <span>错误状态: {!renderError ? '✅ 无错误' : '❌ 有错误'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ThreeTest; 